export const MAX_SSE_BUFFER_BYTES = 256 * 1024;

export type SseMessage = {
  event: string;
  data: string;
};

export class SseBufferOverflowError extends Error {
  constructor() {
    super("SSE event buffer exceeded its safety limit.");
    this.name = "SseBufferOverflowError";
  }
}

/** Incremental SSE parser. It deliberately ignores comments and unknown events. */
export class SseParser {
  private buffer = "";
  private eventName = "message";
  private dataLines: string[] = [];

  push(chunk: string): SseMessage[] {
    this.buffer += chunk;
    if (this.buffer.length > MAX_SSE_BUFFER_BYTES) {
      throw new SseBufferOverflowError();
    }
    const messages: SseMessage[] = [];
    let lineEnd = this.findLineEnd();
    while (lineEnd !== -1) {
      const line = this.buffer.slice(0, lineEnd.index);
      this.buffer = this.buffer.slice(lineEnd.nextIndex);
      this.consumeLine(line, messages);
      lineEnd = this.findLineEnd();
    }
    return messages;
  }

  finish(): SseMessage[] {
    if (this.buffer.length > 0) {
      const messages: SseMessage[] = [];
      this.consumeLine(this.buffer, messages);
      this.buffer = "";
      return messages;
    }
    return [];
  }

  private findLineEnd(): { index: number; nextIndex: number } | -1 {
    for (let index = 0; index < this.buffer.length; index += 1) {
      const character = this.buffer[index];
      if (character === "\n") return { index, nextIndex: index + 1 };
      if (character === "\r") {
        if (index + 1 === this.buffer.length) return -1;
        return {
          index,
          nextIndex: this.buffer[index + 1] === "\n" ? index + 2 : index + 1,
        };
      }
    }
    return -1;
  }

  private consumeLine(line: string, messages: SseMessage[]): void {
    if (line === "") {
      if (this.dataLines.length > 0) {
        messages.push({ event: this.eventName, data: this.dataLines.join("\n") });
      }
      this.eventName = "message";
      this.dataLines = [];
      return;
    }
    if (line.startsWith(":")) return;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") this.eventName = value;
    if (field === "data") {
      this.dataLines.push(value);
      if (this.dataLines.join("\n").length > MAX_SSE_BUFFER_BYTES) {
        throw new SseBufferOverflowError();
      }
    }
  }
}

export function parseSseChunks(chunks: Iterable<string>): SseMessage[] {
  const parser = new SseParser();
  const messages: SseMessage[] = [];
  for (const chunk of chunks) messages.push(...parser.push(chunk));
  messages.push(...parser.finish());
  return messages;
}
